// Functions for performing validation on input fields that are generic in nature. Input validation for fields that are specific to a feature are defined within a given component.

import { ValidationError } from 'Models/ErrorTypes';

// If updating this list, be sure to update the corresponding list on the server-side.
const VALID_FILE_EXTENSIONS = [
    // Plaintext
    '.txt',
    '.csv',
    '.xml',
    '.rtf',
    '.info',
    '.json',
    // Office/PDF
    '.doc',
    '.docx',
    '.xls',
    '.xlsx',
    '.ppt',
    '.pptx',
    '.pdf',
    // Images
    '.bmp',
    '.gif',
    '.jpg',
    '.jpeg',
    '.png',
    '.tif',
    '.tiff',
    // Compressed/Archived
    '.zip',
    '.tar',
    '.gz',
    '.tgz',
];

// If updating this list, be sure to update the corresponding list on the server-side.
const VALID_MIME_TYPES = [
    // Plaintext
    'text/plain',
    'text/csv',
    'text/xml',
    'application/xml',
    'application/rtf',
    'application/json',
    // Office/PDF
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'application/pdf',
    // Images
    'image/bmp',
    'image/x-ms-bmp',
    'image/gif',
    'image/jpeg',
    'image/png',
    'image/tiff',
    'image/tiff-fx',
    // Zip
    'application/zip',
    'application/x-zip-compressed',
    // Gzip
    'application/gzip',
    'application/x-gzip',
    // Tar
    'application/x-tar',
];

const getFileExtension = (filename: string): string | undefined => {
    const hasFileExtension = filename.includes('.') && !filename.endsWith('.');
    return hasFileExtension ? `.${filename.toLowerCase().split('.').pop()}` : undefined;
};

// Confirm a string is a valid URL. At the moment we only care that it contains a protocol at the beginning. At some point in the future this may be expanded to be more strict.
// Being pretty liberal with the definition of a protocol. Everything I can think of should be covered by this (http, https, smb, file, ftp, ftps, sftp, scp, etc...)
// Ensure this matches Common/hps_common/declarative2/hps/dataclass_validation.py/URL_REGEX in the back-end code.
export const validateUrl = (url: string): { valid: boolean; message: string } => {
    if (/^[a-zA-Z]{2,6}:\/\//.test(url) === false) {
        return { valid: false, message: 'URL must start with a protocol, such as https://' };
    } else {
        return { valid: true, message: '' };
    }
};

// File size limitations occur here, when the presigned POST/URL is generated, and when the file is processed on the server-side.
export const validateFileForUpload = (file: File): void => {
    const filename = file.name;
    const fileExtension = getFileExtension(filename);
    if (fileExtension && !VALID_FILE_EXTENSIONS.includes(fileExtension)) {
        throw new ValidationError(`${filename} does not have a valid file extension. Valid file types are plaintext, Office/PDF, images, and archives.`);
    }

    if (!VALID_MIME_TYPES.includes(file.type)) {
        throw new ValidationError(`${filename} does not have a valid type. Valid types are plaintext, Office/PDF, images, and archives.`);
    }

    if (file.size < 1 || file.size > 52428800) {
        throw new ValidationError(`${filename} does not have a valid size. Must not be empty and must not be over 50 MiB.`);
    }

    // Rejects characters that Windows forbids in file names, plus null bytes and ASCII control characters.
    // Windows is the most restrictive OS and its rules are a superset of macOS and Linux restrictions.
    // Note: macOS's default names for screenshots include a non-breaking space (hexadecimal: e2 80 af) between the time and "AM" or "PM". This RegEx allows that, but it's something to keep in mind if we change this.
    // eslint-disable-next-line no-control-regex
    if (/[\x00-\x1f\x7f\\/:*?"<>|]/.test(filename) || filename.trim() === '') {
        throw new ValidationError(`${filename} does not have a valid name. File names cannot contain \\ / : * ? " < > | or control characters, and cannot be blank.`);
    }
};

export const validateEmail = (email: string): boolean => {
    return /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(email);
};

export const validateEin = (ein: string): boolean => {
    return /^[0-9-]*$/.test(ein);
};

/**
 * Verifies that a password:
 *   Is between 8 and 128 characters.
 *      If the password is 20 characters or more, it is considered valid.
 *      If the password is between 16 and 19 characters, it must contain both upper and lower case letters.
 *      If the password is between 13 and 15 characters, it must contain upper and lower case letters and a number.
 *      If the password is between 8 and 12 characters, it must contain upper and lower case letters, a number, and a special character.
 *
 * NIST SP 800-63B should be used for password guidance (https://pages.nist.gov/800-63-3/sp800-63b.html).
 */
export const validatePassword = (password: string): boolean => {
    // Check password length.
    if (/^.{8,128}$/.test(password) === false) {
        return false;
    }

    const passwordLength = password.length;
    if (passwordLength >= 20) {
        return true;
    } else if (passwordLength >= 16) {
        if (/[a-z]/.test(password) && /[A-Z]/.test(password)) {
            return true;
        }
    } else if (passwordLength >= 13) {
        if (/[a-z]/.test(password) && /[A-Z]/.test(password) && /[0-9]/.test(password)) {
            return true;
        }
    } else {
        if (/[a-z]/.test(password) && /[A-Z]/.test(password) && /[0-9]/.test(password) && /[^a-zA-Z0-9]/.test(password)) {
            return true;
        }
    }

    return false;
};
